import {
  liteClient as algoliasearch,
  LiteClient as SearchClient
} from "algoliasearch/lite";
import type { UiState } from "instantsearch.js";
import React, { PropsWithChildren, useEffect, useMemo } from "react";
import { InstantSearch, InstantSearchProps } from "react-instantsearch";
import {
  algoliaUserToken,
  isAlgoliaAnalyticsEnabled
} from "./algoliaAnalytics";
import { BrowserRouteState } from "components/GlobalSearch/routing";
import { isSSR } from "src/utils/ssr";
import { useLocation } from "@reach/router";
import { scrollToSectionId } from "src/utils/inPageNavigation";
import { stringPresent } from "src/utils/strings";
import { InsightsProps } from "instantsearch.js/es/middlewares";

export const buildAlgoliaClient = (): SearchClient => {
  return algoliasearch(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    process.env.GATSBY_ALGOLIA_APP_ID!,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    process.env.GATSBY_ALGOLIA_API_KEY!
  );
};

const searchClient = buildAlgoliaClient();

export type ConfiguredInstantSearchProps = Omit<
  InstantSearchProps<UiState, BrowserRouteState>,
  "searchClient" | "indexName"
> & {
  indexName: string | undefined;
};

export const ConfiguredInstantSearch: React.FC<
  ConfiguredInstantSearchProps
> = ({ children, indexName, ...props }) => {
  const userToken = algoliaUserToken();
  const isTrackingEnabled = isAlgoliaAnalyticsEnabled();

  const insightOptions = useMemo(() => {
    if (!isTrackingEnabled) return false;

    return {
      insightsInitParams: {
        userToken: userToken,
        useCookie: false
      }
    } satisfies InsightsProps;
  }, [isTrackingEnabled, userToken]);

  if (!indexName) return null;

  return (
    <InstantSearch
      {...props}
      indexName={indexName}
      insights={insightOptions}
      searchClient={searchClient}
      future={{
        preserveSharedStateOnUnmount: true,
        persistHierarchicalRootCount: false
      }}
    >
      <FixAnchorLinkWithDelay>{children}</FixAnchorLinkWithDelay>
    </InstantSearch>
  );
};

/*
Since the native anchor link is scrilling the page before the Algolia search results are rendered,
sometimes the page is scrolled to the wrong position. This component will fix the anchor link after
the algolia search is complete and a delay has passed.
*/
const FixAnchorLinkWithDelay: React.FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const targetAnchor = location.hash?.replace("#", "");

  useEffect(() => {
    if (isSSR()) return;

    const timeoutId = setTimeout(() => {
      if (stringPresent(targetAnchor)) scrollToSectionId(targetAnchor);
    }, 2000);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [targetAnchor]);

  return <React.Fragment>{children}</React.Fragment>;
};
